import { generateId } from '../utils/generate-id';
import { Plus } from 'lucide-react';
import React from 'react';
import { ConditionGroup } from './ConditionGroup';
import type { BuilderGroup, BuilderState } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';
import { useSegmentReadOnly } from './segment-read-only-context';

interface ConditionGroupListProps {
  state: BuilderState;
  onUpdate: (state: BuilderState) => void;
  disabled?: boolean;
}

export const ConditionGroupList: React.FC<ConditionGroupListProps> = ({
  state,
  onUpdate,
  disabled = false,
}) => {
  const readOnly = useSegmentReadOnly();
  const handleGroupUpdate = (index: number, group: BuilderGroup) => {
    onUpdate({ groups: state.groups.with(index, group) });
  };

  const handleGroupDelete = (index: number) => {
    onUpdate({ groups: state.groups.toSpliced(index, 1) });
  };

  const handleAddGroup = () => {
    const newGroup: BuilderGroup = {
      id: generateId(),
      logic: 'or',
      conditions: [],
      isExpanded: true,
    };
    onUpdate({ groups: [...state.groups, newGroup] });
  };

  return (
    <div className={styles.groupsAndLabel}>
      {state.groups.map((group, i) => (
        <React.Fragment key={group.id}>
          {i > 0 && (
            <div className={styles.groupDivider}>
              <span className={styles.groupDividerLine} />
              {/* TODO(ENG-3682): top-level groups only support AND-join. */}
              <span className={styles.staticConnectorLabel}>AND</span>
              <span className={styles.groupDividerLine} />
              <span className={styles.groupDividerHint}>All groups must match</span>
            </div>
          )}
          <ConditionGroup
            group={group}
            groupNumber={i + 1}
            onUpdate={handleGroupUpdate.bind(null, i)}
            onDelete={handleGroupDelete.bind(null, i)}
            disabled={disabled}
          />
        </React.Fragment>
      ))}
      {!readOnly && (
        <div className={styles.addGroupRow}>
          <button
            type="button"
            className={styles.addGroupBtn}
            onClick={handleAddGroup}
            disabled={disabled}
            aria-label="Add condition group">
            <Plus size={14} />
            Condition Group
          </button>
        </div>
      )}
    </div>
  );
};
